import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Upload } from "lucide-react";

const cls = "w-full bg-[color:var(--ms-warm-white)] border border-[color:var(--ms-border)] focus:border-[color:var(--ms-charcoal)] px-3 py-2.5 text-sm outline-none text-[color:var(--ms-charcoal)]";

const blank = { title: "", slug: "", description: "", cover_image: "", filter_brand: "", filter_category: "", filter_gender: "", filter_age_group: "", filter_on_sale: null, filter_is_rare: null, is_published: true, sort_order: 0 };

const GENDER_OPTIONS = ["", "neonato", "bambino", "bambina", "ragazzo", "ragazza", "unisex"];

export default function AdminCollections() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const fileRef = useRef(null);

    const load = () => api.get("/admin/collections").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const payload = { ...editing, sort_order: parseInt(editing.sort_order || 0) };
            ["filter_brand", "filter_category", "filter_gender", "filter_age_group"].forEach(k => { if (payload[k] === "") payload[k] = null; });
            if (editing.id) await api.put(`/admin/collections/${editing.id}`, payload);
            else await api.post("/admin/collections", payload);
            toast.success("Salvato");
            setEditing(null);
            load();
        } catch (e) { toast.error("Salvataggio fallito"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare collezione?")) return;
        await api.delete(`/admin/collections/${id}`);
        toast.success("Eliminata");
        load();
    };

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setEditing(prev => ({ ...prev, cover_image: r.data.url }));
    };

    const startEdit = (c) => setEditing({
        ...blank, ...c,
        filter_brand: c.filter_brand || "",
        filter_category: c.filter_category || "",
        filter_gender: c.filter_gender || "",
        filter_age_group: c.filter_age_group || "",
    });

    return (
        <div data-testid="admin-collections">
            <div className="flex justify-between items-center mb-10">
                <div>
                    <div className="overline mb-2">Curation</div>
                    <h1 className="font-display text-4xl text-[color:var(--ms-charcoal)]">Collezioni</h1>
                </div>
                <button onClick={() => setEditing({ ...blank })} className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-5 py-3 text-xs tracking-[0.2em] uppercase flex items-center gap-2 hover:bg-[color:var(--ms-sage-dark)]" data-testid="collection-new"><Plus size={14} /> Nuova collezione</button>
            </div>

            <div className="panel divide-y divide-[color:var(--ms-border)]">
                {items.map(c => (
                    <div key={c.id} className="px-4 py-3 flex items-center gap-4" data-testid={`collection-row-${c.id}`}>
                        {c.cover_image ? <img src={c.cover_image} alt="" className="w-12 h-12 object-cover bg-[color:var(--ms-cream)]" /> : <div className="w-12 h-12 bg-[color:var(--ms-cream)]" />}
                        <div className="flex-1">
                            <div className="text-[color:var(--ms-charcoal)]">{c.title}</div>
                            <div className="text-xs text-[color:var(--ms-muted)]">/{c.slug} · {c.filter_gender ? `${c.filter_gender}` : c.filter_brand || c.filter_category || (c.filter_is_rare ? "Rare" : c.filter_on_sale ? "Outlet" : "Tutte")}</div>
                        </div>
                        <button onClick={() => startEdit(c)} className="p-2 hover:text-[color:var(--ms-sage-dark)]"><Edit2 size={14} /></button>
                        <button onClick={() => remove(c.id)} className="p-2 hover:text-[color:var(--ms-terracotta)]"><Trash2 size={14} /></button>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4">
                    <div className="bg-[color:var(--ms-warm-white)] border border-[color:var(--ms-border)] max-w-xl w-full my-8 p-8 space-y-4" data-testid="collection-editor">
                        <h2 className="font-display text-3xl text-[color:var(--ms-charcoal)]">{editing.id ? "Modifica collezione" : "Nuova collezione"}</h2>
                        <div><label className="overline block mb-2">Titolo</label><input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className={cls} data-testid="coll-title" /></div>
                        <div><label className="overline block mb-2">Slug (automatico se vuoto)</label><input value={editing.slug || ""} onChange={e => setEditing({ ...editing, slug: e.target.value })} className={cls} /></div>
                        <div><label className="overline block mb-2">Descrizione</label><textarea rows={3} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} className={cls} /></div>
                        <div><label className="overline block mb-2">Immagine di copertina</label>
                            <div className="flex gap-2">
                                <input value={editing.cover_image} onChange={e => setEditing({ ...editing, cover_image: e.target.value })} className={cls} />
                                <button onClick={() => fileRef.current?.click()} className="border border-[color:var(--ms-border)] px-3 hover:border-[color:var(--ms-charcoal)]"><Upload size={14} /></button>
                                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="overline block mb-2">Chi indossa</label>
                                <select value={editing.filter_gender || ""} onChange={e => setEditing({ ...editing, filter_gender: e.target.value })} className={cls}>
                                    {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g || "—"}</option>)}
                                </select>
                            </div>
                            <div><label className="overline block mb-2">Categoria</label><input value={editing.filter_category || ""} onChange={e => setEditing({ ...editing, filter_category: e.target.value })} className={cls} placeholder="es. Completi, Calzature" /></div>
                            <div><label className="overline block mb-2">Brand</label><input value={editing.filter_brand || ""} onChange={e => setEditing({ ...editing, filter_brand: e.target.value })} className={cls} /></div>
                            <div><label className="overline block mb-2">Fascia età</label><input value={editing.filter_age_group || ""} onChange={e => setEditing({ ...editing, filter_age_group: e.target.value })} className={cls} placeholder="es. 2-4 anni" /></div>
                        </div>
                        <div className="flex gap-4 text-sm flex-wrap">
                            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.filter_on_sale} onChange={e => setEditing({ ...editing, filter_on_sale: e.target.checked ? true : null })} className="accent-[color:var(--ms-sage-dark)]" /> Solo outlet</label>
                            <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_published} onChange={e => setEditing({ ...editing, is_published: e.target.checked })} className="accent-[color:var(--ms-sage-dark)]" /> Pubblicata</label>
                        </div>
                        <div><label className="overline block mb-2">Ordine di visualizzazione</label><input type="number" value={editing.sort_order} onChange={e => setEditing({ ...editing, sort_order: e.target.value })} className={cls} /></div>
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => setEditing(null)} className="border border-[color:var(--ms-border)] hover:border-[color:var(--ms-charcoal)] px-4 py-2 text-xs tracking-[0.2em] uppercase">Annulla</button>
                            <button onClick={save} className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-5 py-2 text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--ms-sage-dark)]" data-testid="coll-save">Salva</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
